import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Shield, Warning, Heart, Stack, Sparkle } from "@phosphor-icons/react";
import { PageHeader } from "../components/ui/kaizen";

const TECH_LABEL = (v) => {
  if (v === true) return "Permis";
  if (v === false) return "Interdit";
  if (v === "limited_no_12_6") return "Limité (pas 12-6)";
  return String(v);
};
const TECH_COLOR = (v) => v === true ? "#2A9D8F" : (v === false ? "#D90429" : "#E9C46A");

export default function ReglementsModule() {
  const [data, setData] = useState(null);
  const [filterAge, setFilterAge] = useState("all");

  useEffect(() => {
    api.get("/reglements").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="p-12 text-sm text-muted-foreground">Chargement…</div>;

  const rows = filterAge === "all" ? data.master_table : data.master_table.filter((r) => r.age_id === filterAge);

  return (
    <div data-testid="reglements-page">
      <div className="px-8 lg:px-12 pt-8">
        <PageHeader title={<>Tableau maître officiel</>} subtitle={<>Muay Thaï · Kickboxing · K-1 — base MTO 2024 alignée IFMA. Source légale pour validation des athlètes au Québec.</>} />
      </div>

      <div className="px-8 lg:px-12 py-8 space-y-8 max-w-7xl">

        {/* Filtre âge */}
        <div className="flex flex-wrap items-center gap-2" data-testid="age-filter">
          <span className="ticker text-muted-foreground mr-2">Filtrer par âge :</span>
          <FilterBtn active={filterAge === "all"} onClick={() => setFilterAge("all")} label="Toutes"/>
          {data.age_categories.map((a) => (
            <FilterBtn key={a.id} active={filterAge === a.id} onClick={() => setFilterAge(a.id)} label={a.label}/>
          ))}
        </div>

        {/* Tableau maître */}
        <div className="hard-border overflow-x-auto" data-testid="master-table">
          <div className="px-5 py-3 bg-foreground text-white ticker flex items-center gap-2">
            <Stack size={14}/> Tableau maître · {rows.length} ligne{rows.length > 1 ? 's' : ''}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-secondary/40">
              <tr className="ticker text-muted-foreground">
                <th className="text-left px-4 py-3">Âge</th>
                <th className="text-left px-4 py-3">Classe</th>
                <th className="text-left px-4 py-3">Frappes tête</th>
                <th className="text-left px-4 py-3">Genoux tête</th>
                <th className="text-left px-4 py-3">Coudes tête</th>
                <th className="text-left px-4 py-3">Format</th>
                <th className="text-left px-4 py-3">Notes</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.age_id}-${r.class_id}`} className="border-t border-border" data-testid={`row-${r.age_id}-${r.class_id}`}>
                  <td className="px-4 py-3 font-semibold">{r.age_label}</td>
                  <td className="px-4 py-3">
                    <span className="hard-border px-2 py-0.5 ticker bg-secondary/40">{r.class_label}</span>
                  </td>
                  <td className="px-4 py-3"><Pill v={r.head_punches_kicks}/></td>
                  <td className="px-4 py-3"><Pill v={r.knees_to_head}/></td>
                  <td className="px-4 py-3"><Pill v={r.elbows_to_head}/></td>
                  <td className="px-4 py-3 font-mono">{r.rounds && r.minutes ? `${r.rounds} × ${r.minutes} min` : "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Classes d'expérience */}
        <Section icon={Sparkle} title="Classes d'expérience (progression officielle)">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-0 hard-border">
            {data.experience_classes.map((c, i) => (
              <div key={c.id} className={`p-4 ${i < data.experience_classes.length - 1 ? 'md:border-r' : ''} border-b md:border-b-0 border-border`}>
                <div className="ticker text-muted-foreground">{c.label}</div>
                <div className="font-display font-black text-xl mt-1">
                  {c.min_fights}
                  {c.max_fights !== null && c.max_fights !== undefined ? `–${c.max_fights}` : "+"}
                </div>
                <div className="ticker text-muted-foreground mt-1">combats</div>
                {c.note && <div className="text-xs text-muted-foreground mt-2">{c.note}</div>}
              </div>
            ))}
          </div>
        </Section>

        {/* Fautes globales */}
        <Section icon={Warning} title="Fautes interdites en tout temps" accent="#D90429">
          <div className="hard-border p-5 grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-6 text-sm">
            {data.global_fouls.map((f, i) => (
              <div key={i} className="flex items-start gap-2"><span className="text-[#D90429] font-bold mt-0.5">×</span> {f}</div>
            ))}
          </div>
        </Section>

        {/* Exigences médicales */}
        <Section icon={Heart} title="Exigences médicales (tournoi sanctionné)">
          <div className="hard-border p-5 space-y-2 text-sm">
            {data.medical_requirements.map((r, i) => (
              <div key={i} className="flex items-start gap-2"><span className="text-[#2A9D8F] font-bold mt-0.5">✓</span> {r}</div>
            ))}
          </div>
        </Section>

        {/* Progression */}
        <Section icon={Shield} title="Logique de progression interne du club">
          <div className="hard-border p-5 space-y-3 text-sm">
            <div>
              <div className="ticker text-muted-foreground">Base</div>
              <div className="text-muted-foreground">{data.progression_rules.fights_count_basis}</div>
            </div>
            <div>
              <div className="ticker text-muted-foreground mb-2">Critères d'upgrade</div>
              <ul className="space-y-1 text-muted-foreground">
                {data.progression_rules.upgrade_criteria.map((c, i) => <li key={i}>— {c}</li>)}
              </ul>
            </div>
            <div>
              <div className="ticker text-muted-foreground mb-2">Règles de réévaluation</div>
              <div className="space-y-2">
                {data.progression_rules.reevaluation.map((r, i) => (
                  <div key={i} className="hard-border p-3 bg-secondary/30">
                    <div className="font-semibold text-sm">{r.trigger}</div>
                    <div className="text-xs text-muted-foreground mt-1">→ {r.action}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Section>

      </div>
    </div>
  );
}

function FilterBtn({ active, onClick, label }) {
  return (
    <button onClick={onClick}
      className={`hard-border px-3 py-1.5 ticker transition-colors ${active ? 'bg-foreground text-white border-foreground' : 'hover:bg-secondary/40'}`}>
      {label}
    </button>
  );
}

function Section({ icon: Icon, title, children, accent }) {
  return (
    <section>
      <h2 className="font-display font-black text-2xl tracking-tight mb-3 flex items-center gap-2" style={accent ? { color: accent } : {}}>
        <Icon size={20}/> {title}
      </h2>
      {children}
    </section>
  );
}

function Pill({ v }) {
  return (
    <span className="font-semibold text-sm" style={{ color: TECH_COLOR(v) }}>
      {TECH_LABEL(v)}
    </span>
  );
}
